import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DsTypeaheadComponent, DsTypeaheadOption } from '@levelaccess/design-system';

import { CommonUtility } from '../../utility/common.utility';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';

/**
 * Single-select typeahead form component built on top of the design system's `<ds-typeahead>`.
 * Use when the field stores at most one selected object value (e.g. a Jira team) and the candidate
 * values must be retrieved dynamically per keystroke from a server endpoint rather than pre-loaded.
 *
 * Required inputs:
 *   - `fetchSuggestions`: `(term) => Observable<T[]>` server-side search function.
 *   - `valueToLabel`: `(value) => string` used to render an item in the dropdown and in the
 *     selected input.
 *   - `valueToId`: `(value) => string` used for equality when re-rendering a previously stored
 *     value or comparing against fetched suggestions.
 *
 * Form integration: registers as a ControlValueAccessor. The form control's value is `T | null`.
 *
 * Invalid-input handling: if the user types text and leaves the field without selecting an
 * option, the transient typed text is discarded. The form value remains the last committed
 * selection (or `null` if nothing was selected), matching the design system typeahead's visual
 * fallback on blur.
 */
@Component({
  selector: 'app-single-select-typeahead',
  templateUrl: './single-select-typeahead.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SingleSelectTypeaheadComponent),
    },
  ],
})
export class SingleSelectTypeaheadComponent<T> implements ControlValueAccessor, AfterViewChecked {
  protected readonly SharedCommonUtility: typeof SharedCommonUtility = SharedCommonUtility;
  private readonly uniqueId: string = CommonUtility.createUniqueDOMId();
  private onChange: (value: T | null) => void;
  private onTouched: () => void;
  private currentSearchTerm: string = '';

  @Input() public id: string;
  @Input() public required: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public placeholder: string = '';
  @Input() public minLengthForSearch: number = 1;
  @Input() public fetchOnFocus: boolean = false;
  @Input() public openOnFocus: boolean = false;
  @Input() public formControlName: string;
  @Input() public form: UntypedFormGroup;
  @Input() public formValidationRequest$: Observable<void>;
  @Input() public label: string = '';
  @Input() public noLabel: boolean = false;
  @Input() public fetchSuggestions: (term: string) => Observable<T[]>;
  @Input() public valueToLabel: (value: T) => string = (v: T): string => (v ? String(v) : '');
  @Input() public valueToId: (value: T) => string = (v: T): string => (v ? String(v) : '');

  public selectedValue: T | null = null;

  @ViewChild(DsTypeaheadComponent) private dsTypeahead?: DsTypeaheadComponent<T>;

  public constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  public get domID(): string {
    return this.id || this.uniqueId;
  }

  /**
   * Callers such as the required-fields table render the visible name in a separate
   * `<label for="id">` and set `noLabel`, so ds-typeahead draws no label of its own.
   * Its combobox id is generated internally and the host id would otherwise land on this
   * wrapper div, which a label cannot name. Move the external id onto the combobox.
   */
  public ngAfterViewChecked(): void {
    if (!this.noLabel || SharedCommonUtility.isNullishOrEmpty(this.id)) {
      return;
    }

    const input: HTMLInputElement | undefined = this.dsTypeahead?.inputElement?.nativeElement;
    if (SharedCommonUtility.isNullish(input) || input.id === this.id) {
      return;
    }

    input.id = this.id;
  }

  public dsFetchOptions = (term: string): Observable<DsTypeaheadOption<T>[]> => {
    if (SharedCommonUtility.isNullish(this.fetchSuggestions)) {
      return of([]);
    }
    return this.fetchSuggestions(term).pipe(
      map((items: T[]): DsTypeaheadOption<T>[] =>
        (items ?? []).map((value: T): DsTypeaheadOption<T> => ({ value, label: this.valueToLabel(value) })),
      ),
      catchError((err: unknown): Observable<DsTypeaheadOption<T>[]> => {
        console.error('[SingleSelectTypeaheadComponent.dsFetchOptions]', err);
        return of([]);
      }),
    );
  };

  public dsDisplayFromValue = (value: T | null): string => {
    if (SharedCommonUtility.isNullish(value)) {
      return '';
    }
    return this.valueToLabel(value);
  };

  public dsCompareWith = (a: T, b: T): boolean => {
    if (SharedCommonUtility.isNullish(a) || SharedCommonUtility.isNullish(b)) {
      return a === b;
    }
    return this.valueToId(a) === this.valueToId(b);
  };

  public onSearchTermChanged(term: string): void {
    this.currentSearchTerm = term ?? '';
  }

  public onOptionSelected(value: T): void {
    this.selectedValue = value;
    this.currentSearchTerm = '';
    if (SharedCommonUtility.notNullish(this.onChange)) {
      this.onChange(value);
    }
    if (SharedCommonUtility.notNullish(this.onTouched)) {
      this.onTouched();
    }
    this.changeDetectorRef.detectChanges();
  }

  public onCleared(): void {
    this.selectedValue = null;
    this.currentSearchTerm = '';
    if (SharedCommonUtility.notNullish(this.onChange)) {
      this.onChange(null);
    }
    if (SharedCommonUtility.notNullish(this.onTouched)) {
      this.onTouched();
    }
    this.changeDetectorRef.detectChanges();
  }

  @HostListener('focusout')
  public onFocusOut(): void {
    /**
     * Defer the validity check to the next macrotask. When the user picks a dropdown option
     * the option lives in a CDK overlay outside this host, so focusout fires before the
     * companion `optionSelected` / `cleared` event runs. Deferring lets the selection settle
     * before discarding only unmatched, uncommitted search text.
     */
    setTimeout((): void => {
      const activeElement: Node | null = document.activeElement as Node | null;
      if (activeElement !== null && this.elementRef.nativeElement.contains(activeElement)) {
        return;
      }

      const expectedLabel: string = this.dsDisplayFromValue(this.selectedValue).trim();
      // ds-typeahead clears its own internal searchTerm synchronously on focusout, so reading
      // `this.dsTypeahead.searchTerm` here always sees ''. `currentSearchTerm` is fed by the
      // (searchTermChanged) output, which ds-typeahead only emits on user keystrokes, so it
      // still holds the last text the user typed at the moment of blur.
      const typed: string = (this.currentSearchTerm ?? '').trim();

      if (typed.length === 0 || typed === expectedLabel) {
        return;
      }

      if (SharedCommonUtility.notNullish(this.onTouched)) {
        this.onTouched();
      }

      this.currentSearchTerm = '';
      this.changeDetectorRef.detectChanges();
    }, 0);
  }

  public writeValue(value: T | null): void {
    this.selectedValue = value ?? null;
    this.changeDetectorRef.detectChanges();
  }

  public registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.dsTypeahead?.setDisabledState(isDisabled);
  }
}
